import { springLinear } from "@/lib/desk-motion";

/** Slide: a calm spring that barely passes the tab (~2.5% overshoot). */
export const PILL_SPRING = springLinear(22, 0.76, 8.5);
export const PILL_SLIDE_MS = 460;
export const PILL_STRETCH_MS = 440;
/** How much longer the pill gets per tab width travelled, and its cap. */
const STRETCH_PER_TAB = 0.06;
const STRETCH_MAX = 0.1;
/** The pill hugs the icon and label: at least this wide, padded around. */
export const PILL_MIN_WIDTH = 60;
export const PILL_PADDING_X = 6;

export interface PillBox {
  x: number;
  width: number;
}

/**
 * Where the pill sits on a tab: centred, about as wide as tall
 * (PILL_MIN_WIDTH), wider only to wrap a long label with a little padding,
 * never wider than the tab. `contentWidth` is the label (or icon) width.
 */
export const pillBox = (
  tabLeft: number,
  tabWidth: number,
  contentWidth: number,
): PillBox => {
  const width = Math.min(
    tabWidth,
    Math.max(PILL_MIN_WIDTH, Math.ceil(contentWidth) + 2 * PILL_PADDING_X),
  );
  return { x: tabLeft + (tabWidth - width) / 2, width };
};

/**
 * Peak horizontal scale of the pill while it travels `distance` px, for
 * tabs `width` px apart: one tab away stretches it ~6%, capped at 10%.
 */
export const pillStretch = (distance: number, width: number): number => {
  if (width <= 0) return 1;
  const stretch = (Math.abs(distance) / width) * STRETCH_PER_TAB;
  return 1 + Math.min(STRETCH_MAX, stretch);
};

/**
 * Keyframes of the stretch: the pill lengthens a little (thinning a hair)
 * as it leaves, then lands with a barely visible settle.
 */
export const stretchKeyframes = (peak: number): Keyframe[] => {
  const extra = peak - 1;
  const thin = 1 - extra * 0.2;
  const land = 1 - extra * 0.15;
  return [
    { scale: "1 1", easing: "cubic-bezier(0.3, 0, 0.4, 1)" },
    { scale: `${peak} ${thin}`, offset: 0.32, easing: "ease-in-out" },
    { scale: `${land} ${1 + extra * 0.1}`, offset: 0.68, easing: "ease-out" },
    { scale: "1 1" },
  ];
};

/** The pill's current x (mid-slide included), from its computed translate. */
export const currentX = (element: HTMLElement): number => {
  const value = getComputedStyle(element).translate;
  const x = Number.parseFloat(value);
  return Number.isFinite(x) ? x : 0;
};

/**
 * Moves the pill to `to` with the spring and the stretch, resizing it on
 * the way (a long label's pill is wider). It starts from wherever it is
 * right now, so a slide interrupted by another tap (or a finger release)
 * carries on from mid-way instead of jumping. The final box is written to
 * the style first and nothing is filled, so no animation outlives the
 * motion. `tabWidth` is the distance unit of the stretch.
 */
export const slidePill = (
  element: HTMLElement,
  to: PillBox,
  tabWidth: number,
): void => {
  const fromX = currentX(element);
  const fromWidth = Number.parseFloat(getComputedStyle(element).width);
  for (const animation of element.getAnimations()) animation.cancel();
  element.style.translate = `${to.x}px 0`;
  element.style.width = `${to.width}px`;
  if (typeof element.animate !== "function" || fromX === to.x) return;
  const width = Number.isFinite(fromWidth) ? fromWidth : to.width;
  element.animate(
    [
      { translate: `${fromX}px 0`, width: `${width}px` },
      { translate: `${to.x}px 0`, width: `${to.width}px` },
    ],
    { duration: PILL_SLIDE_MS, easing: PILL_SPRING, fill: "none" },
  );
  element.animate(stretchKeyframes(pillStretch(to.x - fromX, tabWidth)), {
    duration: PILL_STRETCH_MS,
    fill: "none",
  });
};

/** The newly active tab's icon swells, dips and settles. */
export const popIcon = (element: Element | null | undefined): void => {
  if (!element || typeof element.animate !== "function") return;
  element.animate(
    [
      { scale: "1" },
      { scale: "1.08", offset: 0.35, easing: PILL_SPRING },
      { scale: "1" },
    ],
    { duration: 420, easing: "cubic-bezier(0.3, 0, 0.4, 1)", fill: "none" },
  );
};
